// frontend/src/hooks/useDebounce.js
import { useState, useEffect } from 'react';

/**
 * Custom hook that debounces a value.
 * The debounced value will only reflect the latest value when the useDebounce
 * hook has not been called for the specified delay period.
 * @param {any} value - The value to debounce.
 * @param {number} delay - The debounce delay in milliseconds.
 * @returns {any} The debounced value.
 */
function useDebounce(value, delay) {
  // State and setters for debounced value
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(
    () => {
      // Set debouncedValue to value (passed in) after the specified delay
      const handler = setTimeout(() => {
        // console.log(`useDebounce: Setting debounced value to "${value}" after ${delay}ms`);
        setDebouncedValue(value);
      }, delay);

      // Return a cleanup function that will be called every time
      // useEffect is re-called. useEffect will only be re-called
      // if value or delay changes (see the inputs array below).
      // This is how we prevent debouncedValue from changing if value is
      // changed within the delay period. Timeout gets cleared and restarted.
      return () => {
        clearTimeout(handler);
      };
    },
    [value, delay] // Only re-call effect if value or delay changes
  );

  return debouncedValue;
}

export default useDebounce;

// --- Example Usage in a Component ---
/*
import React, { useState, useEffect } from 'react';
import useDebounce from './useDebounce'; // Adjust path

function SearchComponent() {
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500); // Debounce with 500ms delay

  useEffect(() => {
    if (debouncedSearchTerm) {
      // Perform the search API call only when debouncedSearchTerm changes
      console.log('Searching for:', debouncedSearchTerm);
      // api.search(debouncedSearchTerm).then(results => ...);
    } else {
      // Clear search results or do nothing
      console.log('Search term cleared or empty.');
    }
  }, [debouncedSearchTerm]); // Effect runs when debouncedSearchTerm updates

  return (
    <input
      type="search"
      placeholder="Search..."
      value={searchTerm}
      onChange={(e) => setSearchTerm(e.target.value)}
    />
  );
}
*/